import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/**
 * Check if error is a network/timeout error (retryable)
 */
function isNetworkError(error: any): boolean {
  return (
    error?.message === 'Network Error' ||
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ETIMEDOUT' ||
    error?.message?.includes('timeout')
  );
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    username: string | null;
    avatarUrl?: string | null;
    walletAddress?: string | null;
    fid?: number | null;
  };
  needsProfileCreation: boolean;
}

export interface RegisterRequest {
  email: string;
  name: string;
  walletAddress?: string;
}

export const authApi = {
  /**
   * Login or register with Privy
   *
   * Uses 15s timeout with 1 retry for network errors.
   * Total max wait = 30s (same as before), but now resilient to transient network blips.
   *
   * This endpoint involves:
   * 1. Verifying Privy token (external API call)
   * 2. Fetching user data from Privy (external API call)
   * 3. Creating user in database (if new user)
   */
  loginWithPrivy: async (privyToken: string, fid?: number, timezone?: string): Promise<LoginResponse> => {
    const payload = { privyToken, ...(fid && { fid }), ...(timezone && { timezone }) };
    const config = { timeout: 15000 }; // 15s timeout per attempt

    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN_PRIVY, payload, config);
      return response.data;
    } catch (error: any) {
      // Retry once on network/timeout errors
      if (isNetworkError(error)) {
        console.log('[Auth] Network error on login, retrying...');
        // Small delay before retry
        await new Promise((resolve) => setTimeout(resolve, 500));
        const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN_PRIVY, payload, config);
        return response.data;
      }
      throw error;
    }
  },

  // Get current user profile
  getProfile: async () => {
    const response = await apiClient.get(API_ENDPOINTS.AUTH.PROFILE);
    return response.data;
  },

  // Update user profile
  updateProfile: async (data: { name?: string; avatar?: string }) => {
    const response = await apiClient.patch(API_ENDPOINTS.AUTH.PROFILE, data);
    return response.data;
  },

  // Upload avatar
  uploadAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await apiClient.post(API_ENDPOINTS.AUTH.UPLOAD_AVATAR, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Logout
  logout: async () => {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    return response.data;
  },
};
